import React, { useState, useEffect, useMemo } from 'react';
import {
  TextField, Button, Box, Typography, IconButton,
  InputLabel, FormControl, Select, MenuItem, CircularProgress,
  alpha, Paper, Autocomplete, Checkbox, FormControlLabel, InputAdornment, Tooltip, Alert,
  Avatar, Chip,
} from '@mui/material';
import { UploadCloud, Trash2, Building2, Plus, Type, RefreshCw, Tag, X, Package } from 'lucide-react';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions } from './common/modal';
import BusinessProductsModal from './BusinessProductsModal';
import { useDispatch, useSelector } from 'react-redux';
import { createBusiness, updateBusiness, fetchStates, fetchCities } from '../redux/slices/businessSlice';
import { fetchUsers } from '../redux/slices/userSlice';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { getBusinessLogoImageUrl, getBusinessImageUrl } from '../utils/helpers';

export const generateBusinessSlug = (name = '', city = '') => {
  const format = (str) => {
    if (!str) return '';
    return String(str)
      .replace(/[^a-zA-Z0-9&\s-]/g, '')
      .trim()
      .split(/\s+/)
      .join('-')
      .replace(/-+/g, '-')
      .toLowerCase();
  };

  const namePart = format(name);
  const cityPart = format(city);
  return cityPart ? `${namePart}-${cityPart}` : namePart;
};

const QUILL_MODULES = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ['clean']
  ],
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet'
];

const BusinessModal = ({ open, onClose, business, categories, subCategories, countries, states, cities, onSuccess }) => {
  const dispatch = useDispatch();
  const isEdit = !!business;

  // Fallback to Redux store if states or cities props are empty
  const reduxStates = useSelector((state) => state.business?.states);
  const reduxCities = useSelector((state) => state.business?.cities);
  const effectiveStates = useMemo(() => {
    return (Array.isArray(states) && states.length > 0) ? states : (Array.isArray(reduxStates) ? reduxStates : []);
  }, [states, reduxStates]);

  const effectiveCities = useMemo(() => {
    return (Array.isArray(cities) && cities.length > 0) ? cities : (Array.isArray(reduxCities) ? reduxCities : []);
  }, [cities, reduxCities]);

  useEffect(() => {
    if (open) {
      if (!effectiveStates || effectiveStates.length === 0) dispatch(fetchStates());
      if (!effectiveCities || effectiveCities.length === 0) dispatch(fetchCities());
    }
  }, [open, effectiveStates, effectiveCities, dispatch]);

  const [formData, setFormData] = useState({
    business_name: '', slug: '', user_id: '', business_type: 'Wholesaler',
    business_category: '', business_sub_category: [], country: 'India',
    state: '', city: '', zipCode: '', address: '', description: '',
    business_keywords: '', business_start_year: '', website_link: '',
    instagram_link: '', facebook_link: ''
  });

  const [isSlugTouched, setIsSlugTouched] = useState(false);
  const [addAsBrand, setAddAsBrand] = useState(true);

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');

  // Image states
  const [existingImages, setExistingImages] = useState([]);
  const [newImageFiles, setNewImageFiles] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showProductsModal, setShowProductsModal] = useState(false);

  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [usersList, setUsersList] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    if (!isEdit && open) {
      const fetchUserList = async () => {
        try {
          const res = await dispatch(fetchUsers({ page: 1, limit: 30, search: userSearchTerm })).unwrap();
          setUsersList(res.users || []);
        } catch (err) {}
      };
      const timer = setTimeout(() => fetchUserList(), 500);
      return () => clearTimeout(timer);
    }
  }, [userSearchTerm, open, isEdit, dispatch]);

  useEffect(() => {
    if (business && open) {
      const initialSlug = business.slug || '';
      setFormData({
        business_name: business.business_name || '',
        slug: initialSlug || generateBusinessSlug(business.business_name, business.city),
        user_id: business.user_id ? (business.user_id._id || business.user_id) : '',
        business_type: business.business_type
          ? (business.business_type.charAt(0).toUpperCase() + business.business_type.slice(1).toLowerCase())
          : 'Wholesaler',
        business_category: business.business_category || '',
        business_sub_category: business.business_sub_category ? (typeof business.business_sub_category === 'string' ? business.business_sub_category.split(',').map(s => s.trim()) : business.business_sub_category) : [],
        country: business.country || '',
        state: business.state || '',
        city: business.city || '',
        zipCode: business.zipCode || '',
        address: business.address || '',
        description: business.description || '',
        business_keywords: business.business_keywords || '',
        business_start_year: business.business_start_year || '',
        website_link: business.website_link || '',
        instagram_link: business.instagram_link || '',
        facebook_link: business.facebook_link || ''
      });
      setLogoPreview(getBusinessLogoImageUrl(business.business_logo));
      setExistingImages(business.business_images || []);
      setNewImageFiles([]);
      setNewImagePreviews([]);
      setSelectedUser(null);
      setUserSearchTerm('');
      setIsSlugTouched(Boolean(initialSlug));
      setAddAsBrand(!!business.isBrand);
    } else if (open) {
      setFormData({
        business_name: '', slug: '', user_id: '', business_type: 'Wholesaler',
        business_category: '', business_sub_category: [], country: 'India',
        state: '', city: '', zipCode: '', address: '', description: '',
        business_keywords: '', business_start_year: '', website_link: '',
        instagram_link: '', facebook_link: ''
      });
      setLogoPreview('');
      setLogoFile(null);
      setExistingImages([]);
      setNewImageFiles([]);
      setNewImagePreviews([]);
      setSelectedUser(null);
      setUserSearchTerm('');
      setIsSlugTouched(false);
      setAddAsBrand(true);
    }
    setError('');
  }, [business, open]);

  // Clean up Object URLs to avoid memory leaks
  useEffect(() => {
    return () => {
      newImagePreviews.forEach(url => URL.revokeObjectURL(url));
    };
  }, [newImagePreviews]);

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;

    if (name === 'business_name') {
      const trimmedValue = value.trimStart();
      setFormData(prev => ({
        ...prev,
        business_name: trimmedValue,
        slug: !isSlugTouched ? generateBusinessSlug(trimmedValue, prev.city) : prev.slug
      }));
    } else if (name === 'slug') {
      // If user clears the slug, un-touch so auto-suggestion turns back on
      setIsSlugTouched(value.trim() !== '');
      setFormData(prev => ({ ...prev, slug: value.trimStart() }));
    } else if (name === 'business_category') {
      setFormData(prev => ({ ...prev, business_category: value, business_sub_category: [] }));
    } else if (name === 'state') {
      setFormData(prev => ({
        ...prev,
        state: value,
        city: '',
        slug: !isSlugTouched ? generateBusinessSlug(prev.business_name, '') : prev.slug
      }));
    } else if (name === 'city') {
      setFormData(prev => ({
        ...prev,
        city: value,
        slug: !isSlugTouched ? generateBusinessSlug(prev.business_name, value) : prev.slug
      }));
    } else {
      const selectFields = ['business_sub_category', 'business_type', 'country'];
      const isSelect = selectFields.includes(name);
      setFormData(prev => ({ ...prev, [name]: isSelect ? value : value.trimStart() }));
    }
  };

  const handleLogoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleImagesChange = (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const newPreviews = files.map(file => URL.createObjectURL(file));

      setNewImageFiles(prev => [...prev, ...files]);
      setNewImagePreviews(prev => [...prev, ...newPreviews]);
    }
  };

  const removeExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeNewImage = (index) => {
    URL.revokeObjectURL(newImagePreviews[index]);
    setNewImageFiles(prev => prev.filter((_, i) => i !== index));
    setNewImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.business_name) return setError('Business name is required');
    if (!formData.business_sub_category || formData.business_sub_category.length === 0) {
      return setError('At least one Business Sub Category is required');
    }
    if (!isEdit && !logoFile) return setError('Business logo is required for new business');

    setLoading(true);
    try {
      const fd = new FormData();
      // Trim all string fields before submitting
      const t = (v) => String(v ?? "").trim();
      const trimmedFormData = {
        ...formData,
        business_name: t(formData.business_name),
        slug: t(formData.slug) || generateBusinessSlug(formData.business_name, formData.city),
        zipCode: t(formData.zipCode),
        address: t(formData.address),
        business_keywords: t(formData.business_keywords),
        business_start_year: t(formData.business_start_year),
        website_link: t(formData.website_link),
        instagram_link: t(formData.instagram_link),
        facebook_link: t(formData.facebook_link),
      };
      Object.keys(trimmedFormData).forEach(key => {
        if (key === 'business_sub_category' && Array.isArray(trimmedFormData[key])) {
          fd.append(key, trimmedFormData[key].join(', '));
        } else {
          fd.append(key, trimmedFormData[key]);
        }
      });

      // Link category_id and brand_name so brand is automatically and reliably created under category
      const selectedCategoryObj = categories?.find(
        c => c.category_name === trimmedFormData.business_category || String(c._id) === String(trimmedFormData.business_category)
      );
      if (selectedCategoryObj?._id) {
        fd.append('category_id', selectedCategoryObj._id);
      }
      fd.append('brand_name', trimmedFormData.business_name);
      fd.append('add_as_brand', isEdit ? (addAsBrand ? 'true' : 'false') : 'true');

      if (logoFile) fd.append('business_logo', logoFile);

      // Handle images for update
      if (isEdit) {
        fd.append('keep_images', JSON.stringify(existingImages));
      }

      if (newImageFiles.length > 0) {
        newImageFiles.forEach(file => fd.append('business_images', file));
      }

      if (isEdit) {
        await dispatch(updateBusiness({ id: business._id, formData: fd })).unwrap();
      } else {
        await dispatch(createBusiness(fd)).unwrap();
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err || 'Failed to save business');
    } finally {
      setLoading(false);
    }
  };

  const norm = (str) => String(str || '').trim().toLowerCase();

  const selectedStateObj = (Array.isArray(effectiveStates) ? effectiveStates : []).find(
    s => norm(s.state_name) === norm(formData.state) || String(s._id) === String(formData.state)
  );

  const filteredCities = formData.state && Array.isArray(effectiveCities)
    ? Array.from(
        new Map(
          effectiveCities
            .filter(c => {
              if (norm(c.state?.state_name) === norm(formData.state)) return true;
              if (typeof c.state === 'string' && norm(c.state) === norm(formData.state)) return true;
              if (norm(c.state_name) === norm(formData.state)) return true;
              if (selectedStateObj && (
                String(c.state_id) === String(selectedStateObj._id) ||
                String(c.state_id?._id) === String(selectedStateObj._id) ||
                String(c.state?._id) === String(selectedStateObj._id)
              )) return true;
              return false;
            })
            .map(c => [norm(c.city_name), c])
        ).values()
      ).sort((a, b) => (a.city_name || '').localeCompare(b.city_name || ''))
    : (Array.isArray(effectiveCities) ? effectiveCities : []);

  // Ensure current formData.city is always an option in the list so MUI doesn't show blank/out-of-range
  const hasCurrentCityInFiltered = filteredCities.some(c => norm(c.city_name) === norm(formData.city));
  const displayCities = (!hasCurrentCityInFiltered && formData.city)
    ? [{ _id: 'current-city-fallback', city_name: formData.city }, ...filteredCities]
    : filteredCities;

  return (
    <AdminDialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="md"
      fullWidth
    >
      <AdminDialogHeader
        title={isEdit ? 'Edit Business' : 'Create New Business'}
        subtitle={isEdit ? 'Update business profile, contact details and media' : 'Register a new verified business or vendor'}
        icon={Building2}
        iconColor="primary"
        onClose={!loading ? onClose : undefined}
      >
        {isEdit && (
          <Tooltip title="View all connected products for this business">
            <Chip
              icon={<Package size={14} />}
              label={`${business?.productsCount ?? 0} Connected Products`}
              size="small"
              clickable
              color={business?.productsCount > 0 ? "primary" : "default"}
              onClick={() => setShowProductsModal(true)}
              sx={{ fontWeight: 700, cursor: 'pointer' }}
            />
          </Tooltip>
        )}
      </AdminDialogHeader>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        <AdminDialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Box>
            {/* Connected Products Overview Card */}
            {isEdit && (
              <Paper
                elevation={0}
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 4,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
                  borderColor: (t) => alpha(t.palette.primary.main, 0.25),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                    <Package size={20} color="#fff" />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      {business?.productsCount ?? 0} Connected Products
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Products published by this merchant on the Reseller Bazzar catalog.
                    </Typography>
                  </Box>
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<Package size={14} />}
                  onClick={() => setShowProductsModal(true)}
                  sx={{ fontWeight: 700, borderRadius: 1.5, textTransform: 'none' }}
                >
                  View Products ({business?.productsCount ?? 0})
                </Button>
              </Paper>
            )}

            {/* Business Logo Section */}
            <Box sx={{ mb: 5 }}>
              <Typography variant="overline" sx={{ mb: 2, fontWeight: 700, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1, letterSpacing: 1.5 }}>
                <Building2 size={14} /> BUSINESS LOGO
              </Typography>

              <Paper
                elevation={0}
                sx={{
                  width: 140,
                  height: 140,
                  borderRadius: 3,
                  border: '2px dashed',
                  borderColor: 'divider',
                  bgcolor: 'background.neutral',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'info.main',
                    bgcolor: (theme) => alpha(theme.palette.info.main, 0.02),
                  }
                }}
              >
                {logoPreview ? (
                  <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
                    <img src={logoPreview} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 8 }} />
                    <IconButton
                      size="small"
                      onClick={(e) => { e.preventDefault(); setLogoPreview(''); setLogoFile(null); }}
                      sx={{
                        position: 'absolute', top: 6, right: 6,
                        bgcolor: 'rgba(255,255,255,0.95)',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                        '&:hover': { bgcolor: 'error.main', color: 'white' }
                      }}
                    >
                      <X size={14} />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <UploadCloud size={32} color="#94a3b8" />
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, fontWeight: 600, color: '#94a3b8' }}>Upload Logo</Typography>
                  </Box>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                />
              </Paper>
            </Box>

            {/* Basic Info */}
            <Box sx={{ mb: 5 }}>
              <Typography variant="overline" sx={{ mb: 3, fontWeight: 700, color: 'text.secondary', display: 'block', letterSpacing: 1.5 }}>BASIC INFORMATION</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
                <TextField label="Business Name" name="business_name" value={formData.business_name} onChange={handleChange} fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                <TextField
                  label="Slug (URL)"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  fullWidth
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Auto-suggest URL slug from name & city">
                          <IconButton
                            size="small"
                            onClick={() => {
                              const suggested = generateBusinessSlug(formData.business_name, formData.city);
                              setFormData(prev => ({ ...prev, slug: suggested }));
                              setIsSlugTouched(false);
                            }}
                            edge="end"
                            sx={{
                              color: 'primary.main',
                              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                              '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16) }
                            }}
                          >
                            <RefreshCw size={16} />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    )
                  }}
                  helperText={
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mt: 0.5, flexWrap: 'wrap', gap: 1 }}>
                      <Box component="span" sx={{ color: formData.slug ? 'info.main' : 'text.secondary', fontSize: '0.75rem', fontWeight: formData.slug ? 600 : 400 }}>
                        {formData.slug ? `URL: /business/${formData.slug}` : "Auto-generated from business name and city"}
                      </Box>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          const suggested = generateBusinessSlug(formData.business_name, formData.city);
                          setFormData(prev => ({ ...prev, slug: suggested }));
                          setIsSlugTouched(false);
                        }}
                        sx={{ textTransform: 'none', py: 0, px: 0.8, fontSize: '0.72rem', minWidth: 'auto', fontWeight: 700 }}
                      >
                        Auto-suggest URL
                      </Button>
                    </Box>
                  }
                />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 3 }}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select name="business_category" value={formData.business_category} label="Category" onChange={handleChange} sx={{ borderRadius: 2 }}>
                    {categories?.map((cat) => (
                      <MenuItem key={cat._id} value={cat.category_name}>{cat.category_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth required>
                  <InputLabel>Sub Category</InputLabel>
                  <Select multiple name="business_sub_category" value={formData.business_sub_category} label="Sub Category" onChange={handleChange} sx={{ borderRadius: 2 }} renderValue={(selected) => selected.join(', ')}>
                    {(subCategories?.filter(sub => sub.category_id === categories?.find(c => c.category_name === formData.business_category)?._id) || []).map((sub) => (
                      <MenuItem key={sub._id} value={sub.sub_category_name}>
                        {formData.business_sub_category.indexOf(sub.sub_category_name) > -1 ? '✓ ' : ''} {sub.sub_category_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth required>
                  <InputLabel>Type</InputLabel>
                  <Select name="business_type" value={formData.business_type} label="Type" onChange={handleChange} sx={{ borderRadius: 2 }}>
                    {['Wholesaler', 'Retailer', 'Manufacturer', 'Reseller', 'Broker', 'Service Provider']
                      .map(type => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                  </Select>
                </FormControl>

                {!isEdit && (
                  <Autocomplete
                    sx={{ gridColumn: '1 / -1' }}
                    options={usersList}
                    getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.email})`}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    value={selectedUser}
                    onChange={(event, newValue) => {
                      setSelectedUser(newValue);
                      setFormData(prev => ({ ...prev, user_id: newValue ? newValue._id : '' }));
                    }}
                    onInputChange={(event, newInputValue) => {
                      setUserSearchTerm(newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select User (Owner)"
                        required
                        helperText="Search by name or email and select the owner"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option._id}>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">{option.first_name} {option.last_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{option.email} • {option.phone}</Typography>
                        </Box>
                      </li>
                    )}
                  />
                )}

                <FormControlLabel
                  sx={{ gridColumn: '1 / -1', mt: 0.5 }}
                  control={
                    <Checkbox
                      checked={addAsBrand}
                      onChange={(e) => setAddAsBrand(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}>
                      <Tag size={16} color="#6366f1" /> Add this business as a Brand under the selected category
                    </Typography>
                  }
                />
              </Box>
            </Box>

            {/* Location & Details */}
            <Box sx={{ mb: 5 }}>
              <Typography variant="overline" sx={{ mb: 3, fontWeight: 700, color: 'text.secondary', display: 'block', letterSpacing: 1.5 }}>LOCATION & DETAILS</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
                <FormControl fullWidth required>
                  <InputLabel>Country</InputLabel>
                  <Select name="country" value={formData.country} label="Country" onChange={handleChange} sx={{ borderRadius: 2 }}>
                    {countries?.map((c) => (
                      <MenuItem key={c._id} value={c.country_name}>{c.country_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth required>
                  <InputLabel>State</InputLabel>
                  <Select name="state" value={formData.state} label="State" onChange={handleChange} sx={{ borderRadius: 2 }}>
                    {effectiveStates?.map((s) => (
                      <MenuItem key={s._id} value={s.state_name}>{s.state_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth required disabled={!formData.state}>
                  <InputLabel>City</InputLabel>
                  <Select name="city" value={formData.city} label="City" onChange={handleChange} sx={{ borderRadius: 2 }}>
                    {displayCities?.map((c) => (
                      <MenuItem key={c._id} value={c.city_name}>{c.city_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 3, mb: 3 }}>
                <TextField label="Zip Code" name="zipCode" value={formData.zipCode} onChange={handleChange} fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                <TextField label="Start Year" name="business_start_year" value={formData.business_start_year} onChange={handleChange} fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Box>

              <Box sx={{ mb: 3 }}>
                <TextField label="Full Address" name="address" value={formData.address} onChange={handleChange} fullWidth required multiline rows={3} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="overline" sx={{ mb: 1.5, fontWeight: 700, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1, letterSpacing: 1 }}>
                  <Type size={14} /> DESCRIPTION
                </Typography>
                <Box sx={{
                  '& .ql-container': { borderBottomLeftRadius: 8, borderBottomRightRadius: 8, minHeight: 200, fontSize: '1rem', fontFamily: 'inherit' },
                  '& .ql-toolbar': { borderTopLeftRadius: 8, borderTopRightRadius: 8, background: '#f8fafc' },
                  '& .ql-editor': { minHeight: 200 }
                }}>
                  <ReactQuill
                    theme="snow"
                    value={formData.description}
                    onChange={(content) => setFormData(prev => ({ ...prev, description: content }))}
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <TextField label="Keywords" name="business_keywords" value={formData.business_keywords} onChange={handleChange} fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                <TextField label="Website" name="website_link" value={formData.website_link} onChange={handleChange} fullWidth placeholder="https://example.com" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Box>
            </Box>

            {/* Portfolio Images Section */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="overline" sx={{ mb: 3, fontWeight: 700, color: 'text.secondary', display: 'block', letterSpacing: 1.5 }}>PORTFOLIO IMAGES</Typography>

              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
                gap: 2
              }}>
                {/* Existing Images */}
                {existingImages.map((img, index) => (
                  <Paper
                    key={`existing-${index}`}
                    elevation={0}
                    sx={{
                      aspectRatio: '1/1',
                      borderRadius: 3,
                      overflow: 'hidden',
                      border: 1,
                      borderColor: 'divider',
                      position: 'relative',
                      bgcolor: 'background.neutral',
                      '&:hover .delete-btn': { opacity: 1 }
                    }}
                  >
                    <img
                      src={getBusinessImageUrl(img)}
                      alt="portfolio"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <IconButton
                      size="small"
                      className="delete-btn"
                      sx={{
                        position: 'absolute', top: 8, right: 8,
                        bgcolor: alpha('#ef4444', 0.9), color: 'white',
                        opacity: 0, transition: 'all 0.2s',
                        '&:hover': { bgcolor: '#ef4444', transform: 'scale(1.1)' }
                      }}
                      onClick={() => removeExistingImage(index)}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </Paper>
                ))}

                {/* New Images Previews */}
                {newImagePreviews.map((preview, index) => (
                  <Paper
                    key={`new-${index}`}
                    elevation={0}
                    sx={{
                      aspectRatio: '1/1',
                      borderRadius: 3,
                      overflow: 'hidden',
                      border: '2px solid', borderColor: 'info.main',
                      position: 'relative',
                      bgcolor: (theme) => alpha(theme.palette.info.main, 0.05),
                      '&:hover .delete-btn': { opacity: 1 }
                    }}
                  >
                    <img
                      src={preview}
                      alt="new preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <IconButton
                      size="small"
                      className="delete-btn"
                      sx={{
                        position: 'absolute', top: 8, right: 8,
                        bgcolor: alpha('#ef4444', 0.9), color: 'white',
                        opacity: 0, transition: 'all 0.2s',
                        '&:hover': { bgcolor: '#ef4444', transform: 'scale(1.1)' }
                      }}
                      onClick={() => removeNewImage(index)}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                    <Box sx={{
                      position: 'absolute', bottom: 0, left: 0, right: 0,
                      bgcolor: 'rgba(24,144,255,0.9)', py: 0.5,
                      textAlign: 'center'
                    }}>
                      <Typography variant="caption" sx={{ color: 'white', fontWeight: 800, fontSize: '0.6rem' }}>NEW</Typography>
                    </Box>
                  </Paper>
                ))}

                {/* Upload Action */}
                <Box
                  sx={{
                    aspectRatio: '1/1',
                    borderRadius: 3,
                    border: '2px dashed #cbd5e1',
                    bgcolor: 'background.neutral',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                    '&:hover': {
                      borderColor: 'info.main',
                      bgcolor: (theme) => alpha(theme.palette.info.main, 0.02),
                      '& .plus-icon': { color: 'info.main', transform: 'scale(1.1)' }
                    }
                  }}
                >
                  <Box
                    className="plus-icon"
                    sx={{
                      width: 40, height: 40, borderRadius: '50%',
                      bgcolor: 'background.paper', border: 1, borderColor: 'divider',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'text.secondary', transition: 'all 0.2s'
                    }}
                  >
                    <Plus size={24} />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>Add Images</Typography>
                  <input
                    type="file" multiple accept="image/*"
                    onChange={handleImagesChange}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                  />
                </Box>
              </Box>
            </Box>

          </Box>
        </AdminDialogContent>
        <AdminDialogActions>
          <Button onClick={onClose} disabled={loading} variant="outlined" sx={{ fontWeight: 600 }}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading && <CircularProgress size={16} color="inherit" />}
            sx={{ px: 3.5, py: 1, fontWeight: 700, borderRadius: 2 }}
          >
            {loading ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Business')}
          </Button>
        </AdminDialogActions>
      </form>

      <BusinessProductsModal
        open={showProductsModal}
        onClose={() => setShowProductsModal(false)}
        business={business}
      />
    </AdminDialog>
  );
};
export default BusinessModal;
